import { Box, Button, Flex, Heading, SimpleGrid, Text } from "@chakra-ui/react";
import { FC, ReactNode, useEffect } from "react";
import { Link } from "react-router";

// ---------------------------------------------------------------------------
// Shared building blocks reused across all public section pages
// ---------------------------------------------------------------------------

export const PageHero: FC<{
  title: string;
  subtitle: string;
  bg?: string;
}> = ({ title, subtitle, bg = "accentLight" }) => (
  <Box bg={bg} px={{ base: 6, md: 20 }} py={{ base: 16, md: 24 }}>
    <Box maxW="800px" mx="auto" textAlign="center">
      <Heading as="h1" fontSize={{ base: "3xl", md: "5xl" }} color="text" mb={4}>
        {title}
      </Heading>
      <Text fontWeight="400" fontSize={{ base: "md", md: "lg" }} color="gray.600">
        {subtitle}
      </Text>
    </Box>
  </Box>
);

export const FeatureCard: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
  <Box bg="white" border="1px solid" borderColor="gray.100" rounded="large" p={8} boxShadow="sm">
    <Heading as="h3" fontSize="lg" color="primary" mb={3}>
      {title}
    </Heading>
    <Text fontWeight="400" color="gray.700" lineHeight="tall">
      {children}
    </Text>
  </Box>
);

export const PageCta: FC<{ title: string; body: string }> = ({ title, body }) => (
  <Box bg="accent" px={{ base: 6, md: 20 }} py={{ base: 14, md: 20 }} mt={20}>
    <Flex
      direction={{ base: "column", md: "row" }}
      align="center"
      justify="space-between"
      gap={8}
      maxW="1000px"
      mx="auto"
    >
      <Box>
        <Heading as="h2" fontSize={{ base: "2xl", md: "3xl" }} color="white" mb={3}>
          {title}
        </Heading>
        <Text fontWeight="400" color="white">
          {body}
        </Text>
      </Box>
      <Link to="/" style={{ textDecoration: "none", flexShrink: 0 }}>
        <Button variant="primary" size="lg">
          Start vandaag nog
        </Button>
      </Link>
    </Flex>
  </Box>
);

export const PageSection: FC<{ children: ReactNode }> = ({ children }) => (
  <Box px={{ base: 6, md: 20 }} py={{ base: 12, md: 16 }} maxW="1400px" mx="auto">
    {children}
  </Box>
);

export const SectionHeading: FC<{ children: ReactNode }> = ({ children }) => (
  <Heading as="h2" fontSize={{ base: "2xl", md: "3xl" }} color="text" mb={10} textAlign="center">
    {children}
  </Heading>
);

const DEFAULT_TITLE = "BROSTAR - BRO Grondwatermonitoring Software voor Bronhouders";
const DEFAULT_DESCRIPTION =
  "BROSTAR is de Nederlandse SaaS-oplossing voor BRO grondwatermonitoring en grondwatergebruik. Beheer GMW, GMN, GLD, GAR, FRD, GUF en GPD via formulieren. Of koppel jou bron-systeem aan de BRO middels een koppeling via BROSTAR. Ideaal voor waterschappen, gemeenten en provincies.";

export const setMetaDescription = (content: string) => {
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", "description");
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

export const usePageTitle = (title: string, description?: string) => {
  useEffect(() => {
    document.title = `${title} | BROSTAR`;
    if (description) setMetaDescription(description);
    return () => {
      document.title = DEFAULT_TITLE;
      if (description) setMetaDescription(DEFAULT_DESCRIPTION);
    };
  }, [title, description]);
};

export const FeatureGrid: FC<{ children: ReactNode }> = ({ children }) => (
  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
    {children}
  </SimpleGrid>
);
